import {
    Background,
    BackgroundColor,
    Container,
    ContainerType,
    Spacer,
    SpacerVariant,
    backgroundNeutralColorsOptions,
    spacerOptionsAll,
} from "../components/layout"
import { BackgroundVideo, VideoAspectRatio } from "../components/video"
import { Color, TypographyColor } from "../components/typography"
import ImageEditor, { AspectRatio } from "../Image"
import React, { useEffect, useState } from "react"
import { Repeater, types, useAdminContext } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../RedesignTypography"

import { FaqItemRedesignProps } from "./FaqItemRedesign"
import { cn } from "../../shared/utils/cn"

export interface FAQRedesignProps {
    headline: TypographyElement[]
    allClosedOnDefault?: boolean
    embedded?: boolean
    videoBackground?: boolean
    cloudflareVideoId?: string
    cloudflareVideoIdMobile?: string
    background?: { color: BackgroundColor } | BackgroundColor
    spacerTop?: SpacerVariant
    spacerBottom?: SpacerVariant
    repeater?: {
        id: string
        props: Omit<FaqItemRedesignProps, "setIsOpen" | "isOpen" | "index">
        type: "FaqItemRedesign"
    }[]
}

const FAQRedesign: types.Brick<FAQRedesignProps> = ({
    allClosedOnDefault,
    embedded,
    videoBackground,
    cloudflareVideoId,
    cloudflareVideoIdMobile,
    background,
    spacerTop,
    spacerBottom,
}) => {
    const { isAdmin } = useAdminContext()

    const [isOpen, setIsOpen] = useState<number | undefined>(allClosedOnDefault ? undefined : 0)

    useEffect(() => {
        setIsOpen(allClosedOnDefault ? undefined : 0)
    }, [allClosedOnDefault])

    const content = (
        <div className={"sm:grid sm:grid-cols-12 sm:gap-x-3.75 lg:gap-x-5"}>
            <div
                className={cn("sm:col-span-8 sm:col-start-2 md:col-span-4 md:col-start-1", {
                    "xl:col-span-3 xl:col-start-2": embedded,
                })}
            >
                <Color color={videoBackground ? TypographyColor.White : undefined}>
                    <Typography propName="headline" allowList={["S1", "S2", "S3", "sup", "link"]} />
                </Color>
            </div>
            <ul className="mt-3 sm:col-span-10 sm:col-start-2 sm:mt-10 md:col-span-7 md:col-start-6 md:mt-0 xl:col-span-6 xl:col-start-6">
                <Repeater
                    propName="repeater"
                    itemProps={{ setIsOpen, isOpen, light: !!videoBackground } as FaqItemRedesignProps}
                />
            </ul>
        </div>
    )

    if (embedded) {
        return content
    }

    const bgColor = typeof background === "object" ? background.color : background

    if (videoBackground) {
        return (
            <div className="relative" style={isAdmin ? { "--header-height": "0px" } : undefined}>
                <div className="sticky top-header h-screen-w/o-header w-full overflow-hidden">
                    <div className="absolute top-0 h-full w-full sm:hidden">
                        <ImageEditor
                            aspectRatio={AspectRatio["9/19"]}
                            maxWidth={767}
                            className="absolute top-0 h-full w-full object-cover"
                            propName="imageMobile"
                        />
                        <BackgroundVideo id={cloudflareVideoIdMobile} aspectRatio={VideoAspectRatio["9/16"]} />
                    </div>
                    <div className="absolute top-0 hidden h-full w-full sm:block">
                        <ImageEditor
                            aspectRatio={AspectRatio["16/9"]}
                            maxWidth={1920}
                            className="absolute top-0 h-full w-full object-cover"
                            propName="image"
                        />
                        <BackgroundVideo id={cloudflareVideoId} aspectRatio={VideoAspectRatio["16/9"]} />
                    </div>
                    <div className="absolute inset-0 bg-[#381300]/50 backdrop-blur-[17px]" aria-hidden />
                </div>
                <div className="relative -mt-screen-w/o-header min-h-screen-w/o-header">
                    <Container type={ContainerType.Redesign}>
                        <Spacer variant={spacerTop} />
                        {content}
                        <Spacer variant={spacerBottom} />
                    </Container>
                </div>
            </div>
        )
    }

    return (
        <Background color={bgColor}>
            <Container type={ContainerType.Redesign}>
                <Spacer variant={spacerTop} />
                {content}
                <Spacer variant={spacerBottom} />
            </Container>
        </Background>
    )
}

FAQRedesign.schema = {
    name: "Faq Redesign",
    label: "FAQ Redesign",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/FaqRedesign.png`,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "FaqItemRedesign",
                    label: "Item",
                    min: 1,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "allClosedOnDefault",
            label: "All closed on default",
            type: types.SideEditPropType.Boolean,
        },
        {
            name: "videoBackground",
            label: "Video background",
            type: types.SideEditPropType.Boolean,
            show: props => !props.embedded,
        },
        {
            name: "cloudflareVideoId",
            label: "Cloudflare Video ID 16x9",
            type: types.SideEditPropType.Text,
            show: props => !props.embedded && !!props.videoBackground,
        },
        {
            name: "image",
            label: "Image Desktop",
            type: types.SideEditPropType.Image,
            show: props => !props.embedded && !!props.videoBackground,
        },
        {
            name: "cloudflareVideoIdMobile",
            label: "Cloudflare Video ID (Mobile) 9x16",
            type: types.SideEditPropType.Text,
            show: props => !props.embedded && !!props.videoBackground,
        },
        {
            name: "imageMobile",
            label: "Image Mobile",
            type: types.SideEditPropType.Image,
            show: props => !props.embedded && !!props.videoBackground,
        },
        {
            name: "background",
            label: "Background",
            type: types.SideEditPropType.Select,
            show: props => !props.embedded && !props.videoBackground,
            selectOptions: {
                options: backgroundNeutralColorsOptions,
                display: types.OptionsDisplay.Color,
            },
        },
        {
            groupName: "Spacer",
            show: props => !props.embedded,
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
            ],
        },
    ],
    getDefaultProps: (): FAQRedesignProps => ({
        headline: [
            {
                type: "h2",
                children: [{ text: "Lorem ipsum dolor sit amet", S1: true }],
            },
        ],
        allClosedOnDefault: false,
        embedded: false,
        videoBackground: false,
        cloudflareVideoId: "",
        cloudflareVideoIdMobile: "",
        background: { color: BackgroundColor.White },
        spacerTop: SpacerVariant.XL,
        spacerBottom: SpacerVariant.XL,
    }),
}

export default FAQRedesign
